import React from "react";
import PropTypes from "prop-types";
import styles from "./CustomerActivityGraph.module.css";
import { ChevronDown } from "react-ikonate";

import {
  AreaChart,
  Area,
  XAxis,
  ResponsiveContainer,
  ReferenceDot,
  ReferenceLine,
} from "recharts";

export function CustomerActivityGraph({
  data,
  activeDay = "Thu",
  title = "Customer Activity",
  dropdownLabel = "Thursday",
  dropdownOptions = [],
  onDropdownChange,
}) {
  const maxValue = Math.max(...data.map((d) => d.value));
  const firstDay = data[0]?.day;
  const lastDay = data[data.length - 1]?.day;
  const supportsDropdownSelect =
    Array.isArray(dropdownOptions) &&
    dropdownOptions.length > 0 &&
    typeof onDropdownChange === "function";

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        {supportsDropdownSelect ? (
          <label className={styles.dropdown}>
            <select
              className={styles.dropdownSelect}
              value={dropdownLabel}
              onChange={(event) => onDropdownChange(event.target.value)}
              aria-label="Select day"
            >
              {dropdownOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <ChevronDown size={18} className={styles.caret} />
          </label>
        ) : (
          <button className={styles.dropdown}>
            {dropdownLabel}
            <ChevronDown size={18} className={styles.caret} />
          </button>
        )}
      </div>

      <div className={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 20, right: 0, left: 0, bottom: 24 }}
          >
            <defs>
              <linearGradient id="activityGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#243746" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              interval={0}
              padding={{ left: 0, right: 0 }}
              tick={({ x, y, payload }) => {
                const value = payload.value;
                const isActive = value === activeDay;
                const isFirst = value === firstDay;
                const isLast = value === lastDay;
                const tx = isFirst ? x + 20 : isLast ? x - 20 : x;

                return (
                  <text
                    x={tx}
                    y={y + 10}
                    textAnchor="middle"
                    className={styles.xTick}
                    fill={isActive ? "#91B6BB" : "#2E2A2A"}
                    fontWeight={isActive ? 600 : 400}
                  >
                    {value}
                  </text>
                );
              }}
            />

            <ReferenceLine
              segment={[
                { x: activeDay, y: maxValue * 0.08 },
                { x: activeDay, y: maxValue },
              ]}
              stroke="#D9DEE3"
              strokeWidth={14}
              strokeLinecap="round"
            />

            <Area
              type="monotone"
              dataKey="value"
              stroke="#243746"
              fill="url(#activityGradient)"
              strokeWidth={2}
              dot={false}
            />

            {data.map((d) =>
              d.day === activeDay ? (
                <ReferenceDot
                  key={d.day}
                  x={d.day}
                  y={d.value}
                  r={7}
                  fill="#91B6BB"
                  stroke="#ffffff"
                  strokeWidth={3}
                />
              ) : null,
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

CustomerActivityGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    }),
  ).isRequired,
  activeDay: PropTypes.string,
  title: PropTypes.string,
  dropdownLabel: PropTypes.string,
  dropdownOptions: PropTypes.arrayOf(PropTypes.string),
  onDropdownChange: PropTypes.func,
};
